import {
    AudioEntity,
    getThreadMessagesByIdParams,
    getThreadsParams,
    MessageDraftEntity,
    MessageEntity,
    ThreadEntity,
    ThreadRestoreResponse,
    ThreadSyncViewedNotification,
    UserThreadsResponse,
} from '@interpals/api-web';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import i18next from 'i18next';

import api, { showApiErrorToast } from 'api';
import { AccountState } from 'redux/account/reducer';
import { ReduxState } from 'redux/root-reducer';
import { generateTempId } from 'utils/generateTempId';
import { MESSAGES_LIMIT, THREADS_LIMIT } from 'constants/limits';

import {
    DeleteMessageParams,
    MessagesList,
    MessagesListItem,
    ReplyToMessageParams,
    SendAudioMessage,
    SendMessageParams,
    SendPhotoParams,
    TempMessage,
} from './types';
import { selectCurrentThreadDraft } from './selectors';

const findOffset = (messages: MessagesList): string | undefined => {
    if (messages.length > 0) {
        return messages[messages.length - 1].id;
    }

    return undefined;
};

export const getMessages = createAsyncThunk<
    MessageEntity[],
    Omit<getThreadMessagesByIdParams, 'id'> & { threadId: string; userId?: string; isModeration?: boolean },
    { state: ReduxState }
>('get/messages', async ({ threadId, userId, offset, limit, isModeration = false }, { getState }) => {
    const { chat } = getState();

    const params = {
        id: threadId,
        limit: limit ?? MESSAGES_LIMIT,
        offset: offset ?? findOffset(chat.messages.list),
    };

    if (isModeration && !userId) {
        throw new Error('userId is required for moderation messages');
    }

    return (
        isModeration && userId
            ? api.modGetThreadMessagesById({
                  ...params,
                  user_id: userId,
              })
            : api.getThreadMessagesById(params)
    ).then(({ data }) => data);
});

export const sendMessage = createAsyncThunk<
    MessagesListItem,
    SendMessageParams,
    { rejectValue: TempMessage; state: ReduxState }
>('messages/send', async (params: SendMessageParams, { getState, dispatch, rejectWithValue }) => {
    const { chat, account } = getState();

    const { payload, threadId, previewMessage } = params;

    // In case of failed message Resend action or message with attachment
    // we want to skip pushToMainThread action here.
    const shouldPushMessageToMainThread =
        chat.messages.list.some((message) => message.id === payload.id) || payload.attachment?.type === 'photo';
    const messageTempId = payload.id ?? payload.tempId ?? generateTempId();

    const tempMessage = {
        id: messageTempId,
        created: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        thread_id: threadId,
        message: payload.message || '',
        sender_id: account.data?.id,
        // In case of correction message need to map comment string to attachments object
        // to show comment in tempMessage which will be pushed to main thread before sending
        // this doesn't break any other type of messages as comment is optional.
        attachments: payload.attachment ? [{ ...payload.attachment, comment: payload.comment ?? undefined }] : null,
    } as TempMessage;

    !shouldPushMessageToMainThread && dispatch(pushTempMessageToMainThread(tempMessage));

    if (previewMessage) {
        return tempMessage;
    }

    const attachmentId =
        payload.attachment?.type === 'photo'
            ? payload.attachment?.photo?.id
            : payload.attachment?.type === 'reply' || payload.attachment?.type === 'correction'
              ? payload.attachment?.message?.id
              : payload.attachment?.type === 'audio'
                ? payload.attachment?.audio?.id
                : undefined;

    return await api
        .createMessage({
            thread_id: threadId,
            target_id: chat.threads.current?.target_id || undefined,
            message: payload.message ?? '',
            comment: payload.comment,
            attachment_type: payload.attachment?.type,
            attachment_id: attachmentId,
            gif_attachment_url: payload.attachment?.gif?.url,
            tmp_id: messageTempId,
        })
        .catch(() => rejectWithValue(tempMessage));
});

export const sendPhoto = createAsyncThunk<void, SendPhotoParams, { rejectValue: TempMessage; state: ReduxState }>(
    'messages/photo',
    async (params: SendPhotoParams, { getState, dispatch, rejectWithValue }) => {
        const { account } = getState() as { account: AccountState };
        const { file, threadId } = params;

        const url = URL.createObjectURL(file);
        const image = new Image();

        const tempMessage = {
            id: generateTempId(),
            created: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
            thread_id: threadId,
            message: '',
            sender_id: account.data?.id,
            attachments: [{ type: 'photo', photo: { url, width: image.width, height: image.height } }],
        } as TempMessage;

        // Push to main thread message with photo and block this possibility in sendMessage action.
        dispatch(pushTempMessageToMainThread(tempMessage));

        return await api
            .createPhoto({ file })
            .then((photo) => {
                dispatch(
                    sendMessage({
                        threadId,
                        payload: {
                            tempId: tempMessage.id,
                            // @ts-ignore photo type is MediaTokenEntity instead of PhotoEntity.
                            attachment: { type: 'photo', photo },
                        },
                    }),
                );
            })
            .catch(async (err) => {
                showApiErrorToast(err, i18next.t);

                return rejectWithValue(tempMessage as TempMessage);
            });
    },
);

export const sendAudioMessage = createAsyncThunk<void, SendAudioMessage, { state: ReduxState }>(
    'message/audio',
    async (params, { dispatch }) => {
        const { threadId, audioBlobUrl } = params;
        const blob = await fetch(audioBlobUrl).then((r) => r.blob());
        const file = new File([blob], 'audio.webm', { type: 'audio/webm' });

        await api.createAudio({ file }).then((audio: AudioEntity) => {
            dispatch(
                sendMessage({
                    threadId,
                    payload: {
                        attachment: { type: 'audio', audio: audio },
                    },
                }),
            );
        });
    },
);

export interface MarkThreadViewedParams {
    id: string;
    message_id: string;
}

export const getThreads = createAsyncThunk<
    UserThreadsResponse,
    getThreadsParams & { userId?: string; isModeration?: boolean },
    { state: ReduxState }
>('get/threads', async ({ userId, limit, offset, isModeration }, { getState }) => {
    const { chat } = getState();

    const paramsWithDefaults = {
        limit: limit ?? THREADS_LIMIT,
        offset: offset ?? chat.threads.list.length,
    };

    if (isModeration && !userId) {
        throw new Error('userId is required for moderation threads');
    }

    return (
        isModeration && userId
            ? api.modGetThreads({
                  ...paramsWithDefaults,
                  id: userId,
              })
            : api.getThreads(paramsWithDefaults)
    ).then((payload): UserThreadsResponse => payload);
});

export const getThread = createAsyncThunk<void | string, string>('get/thread', async (id: string, { dispatch }) => {
    await api.getUserThreadById({ id }).then((thread) => {
        dispatch(setCurrentThread(thread));
    });
});

export const getThreadByUsername = createAsyncThunk<UserThreadsResponse, string, { state: ReduxState }>(
    'get/thread-by-username',
    async (text: string, { getState }) => {
        const { chat } = getState();

        return await api.searchThreads({ text, limit: THREADS_LIMIT, offset: chat.threads.list.length });
    },
);

export const modGetThreadByOwnerContactUids = createAsyncThunk<
    ThreadEntity | void,
    { contactUid: string; threadOwnerId: string }
>('get/mod-get-thread-by-owner-contact-uids', async ({ contactUid, threadOwnerId }) => {
    return api
        .modGetUserThreadById({ id: contactUid, owner_id: threadOwnerId })
        .catch((err) => showApiErrorToast(err, i18next.t));
});

export const deleteThread = createAsyncThunk<void | string, string, { state: ReduxState }>(
    'delete/thread',
    async (id: string, { getState, dispatch }) => {
        const { chat } = getState();

        await api.deleteThreadById({ id }).then(() => {
            if (chat.threads.current?.id === id) {
                dispatch(clearCurrentThread());
            }
        });

        return id;
    },
);

export const markThreadViewed = createAsyncThunk<void | string, MarkThreadViewedParams>(
    'set/thread-viewed',
    async (params: MarkThreadViewedParams) => {
        const { id, message_id } = params;

        try {
            await api.markThreadViewed({ id, message_id });

            return id;
        } catch (err) {}
    },
);

const typingTimers = new Set();
const TYPING_DELAY = 5000;
export const sendTyping = createAsyncThunk<void, string>('thread/typing', async (id: string) => {
    const alreadyTyping = typingTimers.has(id);

    if (!alreadyTyping) {
        typingTimers.add(id);

        await api.threadTyping({ id, delay: TYPING_DELAY }).then(() => {
            setTimeout(() => {
                typingTimers.delete(id);
            }, TYPING_DELAY);
        });
    }
});

interface SaveDraftParams {
    threadId: string;
    message: string;
}

export const setThreadDraft = createAsyncThunk<MessageDraftEntity, SaveDraftParams, { state: ReduxState }>(
    'set/set-thread-draft',
    async ({ threadId, message }, { getState }) => {
        const lastSavedDraft = selectCurrentThreadDraft(getState());

        // Only call api if message has changed
        if ((lastSavedDraft?.message ?? '') !== message) {
            return api.setThreadDraft({ id: threadId, message });
        }

        return {
            thread_id: threadId,
            message: message,
        };
    },
);

export const deleteMessage = createAsyncThunk<string, DeleteMessageParams, { state: ReduxState }>(
    'message/delete',
    async (params, { getState, dispatch }) => {
        const messagesList = getState().chat.messages.list;
        // Since we are using a reversed array, the last item will be at index 0.
        const isDeletedMessageLast = messagesList.findIndex((message) => message.id === params.id) === 0;

        await api.deleteMessage({ ...params }).then(() => {
            if (isDeletedMessageLast) {
                dispatch(setLastThreadMessage(messagesList[1] as MessageEntity));
            }
        });

        return params.id;
    },
);

export const replyToMessage = createAsyncThunk<void, ReplyToMessageParams, { state: ReduxState }>(
    'message/reply',
    async (params: ReplyToMessageParams, { dispatch }) => {
        const { message, threadId, replyMessage } = params;

        dispatch(
            sendMessage({
                threadId,
                payload: {
                    message,
                    attachment: {
                        type: 'reply',
                        message: { ...replyMessage },
                    },
                },
            }),
        );
    },
);

export const restoreMessages = createAsyncThunk<ThreadRestoreResponse, string>(
    'messages/restore',
    async (threadId: string) => {
        return await api.restoreThreadMessages({ id: threadId });
    },
);

export const setCurrentThread = createAction<ThreadEntity>('thread/current/set');
export const setLastThreadMessage = createAction<MessageEntity>('ws/set-thread-last-message');
export const setLastViewedMessage = createAction<ThreadSyncViewedNotification>('ws/set-last-viewed-message');
export const setMessagesToSend = createAction<string>('message-to-send/set');
export const setReplyMessage = createAction<MessagesListItem>('messages/set-reply-message');
export const setThreadTyping = createAction<string>('ws/set-thread-typing');

export const clearCurrentThread = createAction('thread/current/clear');
export const clearFailedMessageFromMainThread = createAction<string>('messages/clear-failed-message-from-main-thread');
export const clearReplyMessage = createAction('message/reply/clear');
export const clearThreadDraft = createAction('thread-draft/clear');
export const clearThreadTyping = createAction<string>('ws/clear-thread-typing');
export const clearMessages = createAction('messages/clear');
export const clearThreads = createAction('threads/clear');

export const pushTempMessageToMainThread = createAction<TempMessage>('messages/push-temp-message-to-main-thread');

export const WS_NEW_MESSAGE = createAction<MessageEntity>('ws/messages-new-message');
